'use client';

import { createContext, useContext, useState, useCallback, useMemo, ReactNode } from 'react';
import { DashboardType } from '@/config/dashboardWorkflows';

interface Project {
  id: number;
  name: string;
  initials: string;
  type: number;
  status: number;
}

interface Sprint {
  id: number;
  name: string;
  start_date: string | null;
  end_date: string | null;
  total_possible_hours: number;
}

interface ProjectContextType {
  selectedProject: Project | null;
  setSelectedProject: (project: Project | null) => void;
  selectedSprint: Sprint | null;
  setSelectedSprint: (sprint: Sprint | null) => void;
  selectedDashboard: DashboardType | null;
  setSelectedDashboard: (dashboard: DashboardType | null) => void;
}

const ProjectContext = createContext<ProjectContextType | undefined>(undefined);

const STORAGE_KEYS = {
  PROJECT_ID: 'selected_project_id',
  SPRINT_ID: 'selected_sprint_id',
  DASHBOARD: 'selected_dashboard',
};

export function ProjectProvider({ children }: { children: ReactNode }) {
  // Initialize from localStorage
  const [selectedProject, setSelectedProjectState] = useState<Project | null>(null);
  const [selectedSprint, setSelectedSprintState] = useState<Sprint | null>(null);
  const [selectedDashboard, setSelectedDashboardState] = useState<DashboardType | null>(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem(STORAGE_KEYS.DASHBOARD);
      if (saved && (saved === 'gtm' || saved === 'customer' || saved === 'finances' || saved === 'product')) {
        return saved as DashboardType;
      }
    }
    return null;
  });

  // Note: Project and sprint objects are restored in ProjectNavigation and Sidebar
  // components when the data is loaded, not here in the context

  // Persist project to localStorage
  const setSelectedProject = useCallback((project: Project | null) => {
    setSelectedProjectState(project);
    if (typeof window !== 'undefined') {
      if (project) {
        localStorage.setItem(STORAGE_KEYS.PROJECT_ID, project.id.toString());
        // Clear dashboard when selecting project
        localStorage.removeItem(STORAGE_KEYS.DASHBOARD);
        setSelectedDashboardState(null);
      } else {
        localStorage.removeItem(STORAGE_KEYS.PROJECT_ID);
        // Clear sprint when project is cleared
        localStorage.removeItem(STORAGE_KEYS.SPRINT_ID);
      }
    }
  }, []);

  // Persist sprint to localStorage
  const setSelectedSprint = useCallback((sprint: Sprint | null) => {
    setSelectedSprintState(sprint);
    if (typeof window !== 'undefined') {
      if (sprint) {
        localStorage.setItem(STORAGE_KEYS.SPRINT_ID, sprint.id.toString());
      } else {
        localStorage.removeItem(STORAGE_KEYS.SPRINT_ID);
      }
    }
  }, []);

  // Persist dashboard to localStorage
  const setSelectedDashboard = useCallback((dashboard: DashboardType | null) => {
    setSelectedDashboardState(dashboard);
    if (typeof window !== 'undefined') {
      if (dashboard) {
        localStorage.setItem(STORAGE_KEYS.DASHBOARD, dashboard);
        // Clear project and sprint when selecting dashboard
        localStorage.removeItem(STORAGE_KEYS.PROJECT_ID);
        localStorage.removeItem(STORAGE_KEYS.SPRINT_ID);
        setSelectedProjectState(null);
        setSelectedSprintState(null);
      } else {
        localStorage.removeItem(STORAGE_KEYS.DASHBOARD);
      }
    }
  }, []);

  const value = useMemo(() => ({
    selectedProject,
    setSelectedProject,
    selectedSprint,
    setSelectedSprint,
    selectedDashboard,
    setSelectedDashboard
  }), [selectedProject, setSelectedProject, selectedSprint, setSelectedSprint, selectedDashboard, setSelectedDashboard]);

  return (
    <ProjectContext.Provider value={value}>
      {children}
    </ProjectContext.Provider>
  );
}

export function useProject() {
  const context = useContext(ProjectContext);
  if (context === undefined) {
    throw new Error('useProject must be used within a ProjectProvider');
  }
  return context;
}


